import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import { useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FileExplorerItem, DragDropHandlers } from '@features/file-explorer/types'
import TableRow from './Table/TableRow'

interface TableViewProps {
  items: FileExplorerItem[]
  getItemKey: (item: FileExplorerItem) => string
  onItemClick: (item: FileExplorerItem) => void
  onSelectItem: (item: FileExplorerItem, index: number, shiftKey: boolean) => void
  onDelete: (item: FileExplorerItem) => void
  onRename: (item: FileExplorerItem) => void
  onDownload: (item: FileExplorerItem) => void
  onShow: (item: FileExplorerItem) => void
  onManagePermissions: (item: FileExplorerItem) => void
  isLoading: boolean
  dragAndDrop: DragDropHandlers
}

export default function TableView({
  items,
  getItemKey,
  onItemClick,
  onSelectItem,
  onDelete,
  onRename,
  onDownload,
  onShow,
  onManagePermissions,
  isLoading,
  dragAndDrop
}: TableViewProps) {
  const { t } = useTranslation()
  const parentRef = useRef<HTMLDivElement>(null)

  // Set up virtualizer - use parent element for scrolling
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current?.parentElement ?? null,
    estimateSize: () => 57, // Estimated row height in pixels
    overscan: 5, // Render extra rows above and below to prevent flickering during scroll
  })

  return (
    <div
      ref={parentRef}
      className="bg-base-100 overflow-visible relative"
      onDragEnter={dragAndDrop.handleDragEnter}
      onDragOver={dragAndDrop.handleDragOver}
      onDragLeave={dragAndDrop.handleDragLeave}
      onDrop={(e) => dragAndDrop.handleDrop(e)}
      data-testid="table-view-drop-zone"
    >
      {/* Drop Zone Overlay - only show for file uploads, not for item moves */}
      {dragAndDrop.isDragging && !dragAndDrop.draggedItem && (
        <div className="absolute inset-0 bg-primary/10 border-4 border-dashed border-primary z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-base-100 p-6 rounded-lg shadow-lg">
            <Upload className="w-12 h-12 text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-primary">
              {t('fileExplorer.dragDrop.dropZone.title')}
            </p>
            <p className="text-sm text-base-content/70 mt-1">
              {t('fileExplorer.dragDrop.dropZone.subtitle')}
            </p>
          </div>
        </div>
      )}

        <table className={`table table-sm w-full select-none ${isLoading ? 'pointer-events-none opacity-70' : ''}`}>
        <thead>
          <tr className='bg-base-200'>
            <th>{t('fileExplorer.table.name')}</th>
            <th className="w-24">{t('fileExplorer.table.owner')}</th>
            <th className="w-32">{t('fileExplorer.table.sharedWith')}</th>
            <th className="w-40">{t('fileExplorer.table.size')}</th>
            <th className="w-48">{t('fileExplorer.table.dates')}</th>
            <th className="w-16">{t('fileExplorer.table.actions')}</th>
          </tr>
        </thead>
        <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-base-content/50">
                  {t('fileExplorer.table.noItems')}
                </td>
              </tr>
            ) : (
              <>
                {/* Spacer for rows before viewport */}
                {rowVirtualizer.getVirtualItems().length > 0 && (
                  <tr>
                    <td colSpan={6} className='p-0' style={{ height: `${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px` }} />
                  </tr>
                )}

                {/* Render only visible rows */}
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const item = items[virtualRow.index]
                  return (
                    <TableRow
                      key={`${item.type}-${item.id}`}
                      item={item}
                      items={items}
                      index={virtualRow.index}
                      onItemClick={onItemClick}
                      onSelectItem={onSelectItem}
                      onDelete={onDelete}
                      onRename={onRename}
                      onDownload={onDownload}
                      onShow={onShow}
                      onManagePermissions={onManagePermissions}
                      dragAndDrop={dragAndDrop}
                      getItemKey={getItemKey}
                    />
                  )
                })}

                {/* Spacer for rows after viewport */}
                {rowVirtualizer.getVirtualItems().length > 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className='p-0'
                      style={{
                        height: `${
                          rowVirtualizer.getTotalSize() -
                          (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.end ?? 0)
                        }px`
                      }}
                    />
                  </tr>
                )}
              </>
            )}
        </tbody>
      </table>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-base-100/20 flex items-center justify-center z-10 pointer-events-auto">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}
    </div>
  )
}
